/**
 * DataTable Component
 *
 * Generic, reusable sortable/filterable data table with column visibility controls.
 * Supports text filtering across all visible columns, click-to-sort headers,
 * and a column visibility picker dropdown.
 */

import React, { useState, useMemo, useRef, useEffect, ReactNode } from 'react';

export interface Column<T> {
  key: string;
  label: string;
  sortable?: boolean;
  render?: (value: any, row: T) => ReactNode;
  hidden?: boolean;
  width?: string;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  emptyMessage?: string;
  /** Called when a row is clicked. */
  onRowClick?: (row: T, index: number) => void;
  /** Index of row to highlight (e.g. for active selection). */
  highlightRowIndex?: number;
}

type SortDirection = 'asc' | 'desc';

interface SortState {
  key: string;
  direction: SortDirection;
}

function getNestedValue(obj: any, key: string): any {
  return key.split('.').reduce((acc, part) => acc?.[part], obj);
}

function renderCellValue(value: any): ReactNode {
  if (value === null || value === undefined || value === '') {
    return <span className="text-gray-400">-</span>;
  }
  if (typeof value === 'boolean') {
    return (
      <span className={value ? 'text-green-600 font-medium' : 'text-red-500 font-medium'}>
        {String(value)}
      </span>
    );
  }
  return String(value);
}

export function DataTable<T extends Record<string, any>>({
  columns,
  data,
  emptyMessage = 'No data available',
  onRowClick,
  highlightRowIndex,
}: DataTableProps<T>) {
  const [filter, setFilter] = useState('');
  const [sort, setSort] = useState<SortState | null>(null);
  const [hiddenKeys, setHiddenKeys] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    for (const col of columns) {
      if (col.hidden) {
        initial.add(col.key);
      }
    }
    return initial;
  });
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  // Close column picker on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setShowColumnPicker(false);
      }
    };
    if (showColumnPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showColumnPicker]);

  const visibleColumns = useMemo(
    () => columns.filter((col) => !hiddenKeys.has(col.key)),
    [columns, hiddenKeys]
  );

  const filteredData = useMemo(() => {
    if (!filter.trim()) return data;
    const lowerFilter = filter.toLowerCase();
    return data.filter((row) =>
      visibleColumns.some((col) => {
        const val = getNestedValue(row, col.key);
        if (val === null || val === undefined) return false;
        return String(val).toLowerCase().includes(lowerFilter);
      })
    );
  }, [data, filter, visibleColumns]);

  const sortedData = useMemo(() => {
    if (!sort) return filteredData;
    const { key, direction } = sort;
    return [...filteredData].sort((a, b) => {
      const aVal = getNestedValue(a, key);
      const bVal = getNestedValue(b, key);
      if (aVal === bVal) return 0;
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;
      const cmp = typeof aVal === 'number' && typeof bVal === 'number'
        ? aVal - bVal
        : String(aVal).localeCompare(String(bVal));
      return direction === 'asc' ? cmp : -cmp;
    });
  }, [filteredData, sort]);

  const handleSort = (key: string) => {
    setSort((prev) => {
      if (prev?.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const toggleColumn = (key: string) => {
    setHiddenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-200 bg-gray-50 flex-shrink-0">
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter..."
          className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
        <div className="relative" ref={pickerRef}>
          <button
            onClick={() => setShowColumnPicker(!showColumnPicker)}
            className="px-2 py-1 text-xs text-gray-600 border border-gray-300 rounded hover:bg-gray-100"
            title="Toggle columns"
          >
            Columns
          </button>
          {showColumnPicker && (
            <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-gray-200 rounded shadow-lg py-1 w-48 max-h-64 overflow-y-auto">
              {columns.map((col) => (
                <label
                  key={col.key}
                  className="flex items-center gap-2 px-3 py-1 text-xs hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={!hiddenKeys.has(col.key)}
                    onChange={() => toggleColumn(col.key)}
                    className="rounded text-blue-600"
                  />
                  {col.label}
                </label>
              ))}
            </div>
          )}
        </div>
        <span className="text-xs text-gray-500">{sortedData.length} rows</span>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-gray-100 z-10">
            <tr>
              {visibleColumns.map((col) => (
                <th
                  key={col.key}
                  className={`px-2 py-1.5 text-left font-medium text-gray-600 border-b border-gray-200 whitespace-nowrap ${
                    col.sortable ? 'cursor-pointer hover:bg-gray-200 select-none' : ''
                  }`}
                  style={col.width ? { width: col.width, minWidth: col.width } : undefined}
                  onClick={col.sortable ? () => handleSort(col.key) : undefined}
                >
                  <span className="flex items-center gap-1">
                    {col.label}
                    {col.sortable && sort?.key === col.key && (
                      <span className="text-blue-600">
                        {sort.direction === 'asc' ? '\u2191' : '\u2193'}
                      </span>
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedData.length === 0 ? (
              <tr>
                <td
                  colSpan={visibleColumns.length}
                  className="px-4 py-8 text-center text-gray-400"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              sortedData.map((row, idx) => (
                <tr
                  key={idx}
                  className={`border-b border-gray-100 ${
                    highlightRowIndex === idx
                      ? 'bg-blue-100'
                      : 'hover:bg-blue-50/30'
                  } ${onRowClick ? 'cursor-pointer' : ''}`}
                  onClick={onRowClick ? () => onRowClick(row, idx) : undefined}
                >
                  {visibleColumns.map((col) => {
                    const value = getNestedValue(row, col.key);
                    return (
                      <td
                        key={col.key}
                        className="px-2 py-1 text-gray-700 truncate max-w-xs"
                        style={col.width ? { width: col.width, minWidth: col.width } : undefined}
                      >
                        {col.render ? col.render(value, row) : renderCellValue(value)}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
